// Display formatting. Money arrives as a string of minor units and is never turned into a float.

const groupThousands = (digits: string) => {
  let out = '';
  for (let i = 0; i < digits.length; i++) {
    if (i > 0 && (digits.length - i) % 3 === 0) out += ',';
    out += digits[i];
  }
  return out;
};

function digitsOf(minor: string, exponent: number) {
  const neg = minor.startsWith('-');
  const digits = (neg ? minor.slice(1) : minor).padStart(exponent + 1, '0');
  return { neg, number: `${groupThousands(digits.slice(0, digits.length - exponent))}${exponent ? `.${digits.slice(-exponent)}` : ''}` };
}

/** Headline money (docs/06 §3): "180000000" → "HK$ 1,800,000.00"; other currencies "SGD 1,659.99". */
export function money(minor: string | null | undefined, ccy = 'HKD', exponent = 2): string {
  if (!minor || !/^-?\d+$/.test(minor)) return '—';
  const { neg, number } = digitsOf(minor, exponent);
  return `${neg ? '-' : ''}${ccy === 'HKD' ? 'HK$' : ccy} ${number}`;
}

/** Dense tables (docs/06 §3): "4800000", "HKD" → "48,000.00 HKD", in the currency's own exponent. */
export function tableAmount(minor: string | null | undefined, ccy: string): string {
  if (!minor || !/^-?\d+$/.test(minor)) return '—';
  const { neg, number } = digitsOf(minor, EXPONENT[ccy] ?? 2);
  return `${neg ? '-' : ''}${number} ${ccy}`;
}

/** Minor-unit exponents (mirrors the server's money.ts). */
export const EXPONENT: Record<string, number> = { HKD: 2, USD: 2, EUR: 2, SGD: 2, GBP: 2, CNY: 2, INR: 2, AED: 2, AUD: 2, VND: 0, JPY: 0 };

/** Money in its own currency's exponent: amount("500000", "SGD") → "SGD 5,000.00". */
export const amount = (minor: string | null | undefined, ccy: string) => money(minor, ccy, EXPONENT[ccy] ?? 2);

/** An amount typed by a person in `ccy` → minor units string ("1,250.5" SGD → "125050"). Null when not a valid amount. */
export function toMinor(input: string, ccy: string): string | null {
  const exp = EXPONENT[ccy] ?? 2;
  const clean = input.replace(/[,\s]/g, '');
  const re = exp ? new RegExp(`^\\d{1,13}(\\.\\d{1,${exp}})?$`) : /^\d{1,15}$/;
  if (!re.test(clean)) return null;
  const [w, f = ''] = clean.split('.');
  return (BigInt(w!) * 10n ** BigInt(exp) + BigInt((f + '0'.repeat(exp)).slice(0, exp) || '0')).toString();
}

/** Whole HK dollars typed by a person → minor units string ("1,800,000" → "180000000"). Null when not a number. */
export function hkdToMinor(input: string): string | null {
  const clean = input.replace(/[,\s]/g, '');
  if (!/^\d{1,13}(\.\d{1,2})?$/.test(clean)) return null;
  const [w, f = ''] = clean.split('.');
  return (BigInt(w!) * 100n + BigInt((f + '00').slice(0, 2))).toString();
}

export const minorToHkd = (minor: string) => (minor && /^\d+$/.test(minor) ? (BigInt(minor) / 100n).toString() : '');

export function date(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Hong_Kong' });
}

/** docs/06 §6: "05 Sep 2026 · 11:04 HKT", always in Hong Kong time. */
export function dateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Hong_Kong' });
  return `${date(iso)} · ${time} HKT`;
}

/** Whole days since an ISO timestamp. */
export const ageDays = (iso: string | null | undefined, now = Date.now()) => (iso ? Math.floor((now - new Date(iso).getTime()) / 86_400_000) : null);

export const titleCase = (s: string) => s.toLowerCase().replaceAll('_', ' ').replace(/^\w/, c => c.toUpperCase());

/** `days` calendar days after `from`, as YYYY-MM-DD (default due date of a request for information: 14 days, ADR-095). */
export function addDaysIso(from: Date, days: number): string {
  const d = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate()));
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Calendar day `days` business days (Mon–Fri) after `from`, as YYYY-MM-DD. */
export function addBusinessDaysIso(from: Date, days: number): string {
  const d = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate()));
  let added = 0;
  while (added < days) {
    d.setUTCDate(d.getUTCDate() + 1);
    if (d.getUTCDay() !== 0 && d.getUTCDay() !== 6) added++;
  }
  return d.toISOString().slice(0, 10);
}

/** While typing an amount, keeps only digits, thousands commas and one decimal point (at most 2 places). */
export const amountChars = (v: string) => {
  const kept = v.replace(/[^\d.,]/g, '');
  const dot = kept.indexOf('.');
  return dot < 0 ? kept : `${kept.slice(0, dot + 1)}${kept.slice(dot + 1).replace(/[.,]/g, '').slice(0, 2)}`;
};

const regionNames = typeof Intl !== 'undefined' && 'DisplayNames' in Intl ? new Intl.DisplayNames(['en'], { type: 'region' }) : null;
/** "HK" → "Hong Kong". Anything that is not a two-letter country code (e.g. a vendor's "Hong Kong") is returned as it is,
 *  so data from outside (Shufti list entries) can never crash a screen. */
export function countryName(code: string | null | undefined): string | undefined {
  if (!code) return undefined;
  const c = code.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(c) || !regionNames) return code;
  try { return regionNames.of(c) ?? code; } catch { return code; }
}
